<template>
  <div class="mb-4 flex items-center justify-between">
    <h2>{{ t('vehicleCondition') }}</h2>
    <CVButton @click.prevent="handleAddDamage">
      {{ addDamageButtonLabel }}
    </CVButton>
  </div>
  <StatusBanner v-if="hasUnsentDamages" variant="warning" class="mb-4">
    {{ t('unsentBanner', unsentDamages.length) }}
    <template #actions>
      <CVButton
        variant="secondary"
        size="sm"
        :is-loading="isSending"
        :disabled="isSending"
        @click.prevent="handleSendNow"
      >
        {{ t('sendNow') }}
      </CVButton>
    </template>
  </StatusBanner>
  <div class="grid grid-cols-6 gap-4">
    <MarkerContainer
      class="col-span-6 col-start-1 self-start rounded-lg bg-white"
      :marker-positions="
        [...(damages ?? []), ...unsentDamages]
          .filter(
            (d): d is typeof d & { positionX: number; positionY: number } =>
              d.positionX != null && d.positionY != null,
          )
          .map((d) => ({
            positionX: d.positionX,
            positionY: d.positionY,
          }))
      "
    />
    <Tabs v-model="activeTab" :tabs="tabs" class="col-span-6 mt-6" />
    <div class="col-span-6 col-start-1 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
      <UnsentDamageCard v-for="damage in unsentDamages" :key="damage.id" :damage="damage" />
      <DamageCard
        v-for="(damage, i) in damages"
        :key="i"
        :damage="damage"
        :index="i + 1"
        :show-repaired-status="activeTab === Tab.NEW"
        @on-view="viewDamage(damage.id)"
      />
    </div>
  </div>

  <Modal v-model="showNewDamageModal" :close-on-click-outside="false">
    <DamagesForm
      v-if="showNewDamageModal"
      :car-id="carId"
      :booking-id="bookingId"
      @on-close="onCloseNewDamageModal"
    />
  </Modal>

  <DamageViewer
    v-if="viewedDamage"
    :key="viewedDamage?.id"
    v-model="showViewDamageModal"
    :damage="viewedDamage"
  />
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useDamages } from '@/queries/use-damages';
import { computed, ref, watchEffect } from 'vue';
import DamageCard from './components/DamageCard.vue';
import UnsentDamageCard from './components/UnsentDamageCard.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import { useUnsentDamages } from '@/hooks/use-unsent-damages';
import { createToast } from 'mosha-vue-toastify';
import { evidenceUploadQueue } from '@/utils/evidence-upload-queue';
import MarkerContainer from './components/MarkerContainer.vue';
import DamagesForm from './DamagesForm.vue';
import DamageViewer from './DamageViewer.vue';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import { DamageStatus } from '@carvia/ros-client-types';
import { useModalVisibility } from '@/utils/use-modal-visibility';
import Modal from '@/components/Modal.vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

enum Tab {
  ALL = 'ALL',
  NEW = 'NEW',
  REPAIRED = 'REPAIRED',
}
const props = defineProps<{
  carId: string;
  bookingId?: string;
  addDamageButtonText?: string;
}>();

const { t } = useI18n();
const showNewDamageModal = useModalVisibility<boolean>('new-damage');
const showViewDamageModal = useModalVisibility<boolean>('view-damage');
const viewedDamageId = ref<string | null>(null);

const handleAddDamage = () => {
  showNewDamageModal.value = true;
};

const activeTab = ref<Tab>(Tab.ALL);
const tabs = computed<TabItem[]>(() => [
  { title: t('damages'), id: Tab.ALL },
  { title: t('newDamages'), id: Tab.NEW },
  { title: t('repaired'), id: Tab.REPAIRED },
]);

const { data: damagesOfCar } = useDamages({
  carIds: [props.carId],
  withPosition: true,
});
const newDamageIds = ref<string[]>([]);
const { unsentDamages, hasUnsentDamages } = useUnsentDamages(() => props.carId);

const isSending = ref(false);

const showSendToast = (message: string, type: 'success' | 'warning' | 'danger') => {
  createToast(message, { type, position: 'bottom-right' });
};

/**
 * Always tries, since the browser's online flag is not reliable on phones; it only picks the
 * message when the attempt failed.
 */
const handleSendNow = async () => {
  isSending.value = true;
  try {
    const { pending, failed, lastError } = await evidenceUploadQueue.sendUnsentDamagesNow();
    if (failed && !navigator.onLine) showSendToast(t('sendFeedback.offline'), 'warning');
    else if (failed) showSendToast(t('sendFeedback.failed', { error: lastError ?? '' }), 'danger');
    else if (pending) showSendToast(t('sendFeedback.pending'), 'warning');
    else showSendToast(t('sendFeedback.sent'), 'success');
  } finally {
    isSending.value = false;
  }
};

const damages = computed(() => {
  switch (activeTab.value) {
    case Tab.NEW:
      return damagesOfCar.value?.filter(
        (damage) =>
          newDamageIds.value.some((id) => id === damage.id) ||
          damage.booking?.id === props.bookingId,
      );
    case Tab.REPAIRED:
      return damagesOfCar.value?.filter((damage) => damage.status === DamageStatus.REPAIRED);
    default:
      return damagesOfCar.value?.filter((damage) => damage.status === DamageStatus.OPEN);
  }
});

const viewedDamage = computed(() => damages.value?.find((d) => d.id === viewedDamageId.value));

const onCloseNewDamageModal = (newDamageId?: string) => {
  showNewDamageModal.value = false;
  if (!newDamageId) return;
  newDamageIds.value.push(newDamageId);
  activeTab.value = Tab.NEW;
};

const viewDamage = (damageId: string) => {
  showViewDamageModal.value = true;
  viewedDamageId.value = damageId;
};

watchEffect(() => {
  if (
    showViewDamageModal.value &&
    damages.value &&
    !damages?.value?.some((d) => d.id === viewedDamage.value?.id)
  ) {
    showViewDamageModal.value = false;
  }
});

const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');
const addDamageButtonLabel = computed(() =>
  isMobile.value ? t('addDamageShort') : t('addDamage'),
);
</script>

<i18n lang="json">
{
  "en": {
    "description": "Sort Description of Damage",
    "saveImage": "save image",
    "mark": "Mark the damage",
    "damage": "Damage",
    "addDamage": "+ add damage",
    "addDamageShort": "+ damage",
    "damages": "All",
    "newDamages": "New Damages",
    "repaired": "Repaired",
    "vehicleCondition": "Vehicle Condition",
    "unsentBanner": "No connection: 1 damage is saved on this device only and will be sent automatically. | No connection: {n} damages are saved on this device only and will be sent automatically.",
    "sendNow": "Send now",
    "sendFeedback": {
      "offline": "No connection. It will be sent automatically once the device is back online.",
      "failed": "Sending failed: {error}. It will be retried automatically.",
      "pending": "Still sending – other uploads are ahead of it.",
      "sent": "Damage sent."
    }
  },
  "de": {
    "description": "Kurze Beschreibung des Schadens",
    "saveImage": "Bild Speichern",
    "mark": "Markiere den Schaden",
    "damage": "Schaden",
    "addDamage": "+ Schaden hinzufügen",
    "addDamageShort": "+ Schaden",
    "damages": "Alle",
    "newDamages": "Neue Schäden",
    "repaired": "Repariert",
    "vehicleCondition": "Fahrzeugzustand",
    "unsentBanner": "Keine Verbindung: 1 Schaden ist nur auf diesem Gerät gespeichert und wird automatisch gesendet. | Keine Verbindung: {n} Schäden sind nur auf diesem Gerät gespeichert und werden automatisch gesendet.",
    "sendNow": "Jetzt senden",
    "sendFeedback": {
      "offline": "Keine Verbindung. Wird automatisch gesendet, sobald das Gerät wieder online ist.",
      "failed": "Senden fehlgeschlagen: {error}. Wird automatisch erneut versucht.",
      "pending": "Wird noch gesendet – andere Uploads laufen gerade.",
      "sent": "Schaden gesendet."
    }
  },
  "es": {
    "description": "Breve descripción del daño",
    "saveImage": "guardar imagen",
    "mark": "Marcar el daño",
    "damage": "Daño",
    "addDamage": "+ añadir daño",
    "addDamageShort": "+ daño",
    "damages": "Todos",
    "newDamages": "Daños nuevos",
    "repaired": "Reparado",
    "vehicleCondition": "Estado del vehículo",
    "unsentBanner": "Sin conexión: 1 daño solo está guardado en este dispositivo y se enviará automáticamente. | Sin conexión: {n} daños solo están guardados en este dispositivo y se enviarán automáticamente.",
    "sendNow": "Enviar ahora",
    "sendFeedback": {
      "offline": "Sin conexión. Se enviará automáticamente cuando el dispositivo vuelva a estar en línea.",
      "failed": "Error al enviar: {error}. Se reintentará automáticamente.",
      "pending": "Aún enviando: hay otras cargas en curso.",
      "sent": "Daño enviado."
    }
  }
}
</i18n>
